import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { SearchModel } from '@/components/RunsTable/types';
import { usePrevious } from '@/hooks/usePrevious';

export const DEFAULT_FILTER_STRING = 'and(eq(is_root, true))';

// Deep-compare two SearchModel objects (ignores key ordering and tolerates
// extra/unknown properties so we don’t miss equality because we forgot to list
// each field explicitly).
function areSearchModelsEqual(a?: SearchModel, b?: SearchModel): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => {
    return a[key] === b[key];
  });
}

export function useTabAwareFilters(props: {
  runTableFilters?: SearchModel;
  setRunTableFilters?: (filters: SearchModel) => void;
  existingRuleFilters?: SearchModel;
  syncToTab?: '0' | '1';
}): [SearchModel, (filters: SearchModel) => void] {
  const { syncToTab = '0' } = props;

  const [searchParams] = useSearchParams();
  const tracingProjectTab = searchParams.get('tab') ?? syncToTab;
  const { evaluatorId } = useParams();

  const defaultFilter =
    props.existingRuleFilters ??
    (tracingProjectTab === syncToTab
      ? props.runTableFilters ?? {
          filter: DEFAULT_FILTER_STRING,
        }
      : {
          filter: '',
        });

  const [localFilters, setLocalFilters] = useState<SearchModel>(defaultFilter);

  const { runTableFilters, setRunTableFilters } = props;
  const filtersToUse =
    tracingProjectTab === syncToTab && !evaluatorId
      ? runTableFilters ?? localFilters
      : localFilters;

  const prevRunTableFilters = usePrevious(runTableFilters);
  const prevTracingProjectTab = usePrevious(tracingProjectTab);

  useEffect(() => {
    // only update local filters to sync if the table filters are changed
    // from the outside.
    if (
      tracingProjectTab === syncToTab &&
      prevRunTableFilters &&
      runTableFilters &&
      !areSearchModelsEqual(prevRunTableFilters, runTableFilters) &&
      !areSearchModelsEqual(runTableFilters, localFilters)
    ) {
      setLocalFilters(runTableFilters);
    }

    // Only set the run table filters if we're switching
    // from a different tab to the tracing project tab
    if (
      tracingProjectTab === syncToTab &&
      prevTracingProjectTab !== syncToTab &&
      !areSearchModelsEqual(localFilters, runTableFilters)
    ) {
      setRunTableFilters?.(localFilters);
    }
  }, [
    prevRunTableFilters,
    runTableFilters,
    tracingProjectTab,
    localFilters,
    prevTracingProjectTab,
    setRunTableFilters,
    syncToTab,
  ]);

  const handleSetFilters = (filters: SearchModel) => {
    setLocalFilters(filters);
    if (tracingProjectTab === syncToTab && !evaluatorId) {
      if (!areSearchModelsEqual(filters, runTableFilters)) {
        setRunTableFilters?.(filters);
      }
    }
  };

  return [filtersToUse, handleSetFilters];
}
